import {
  ChangeDetectorRef,
  Component,
  inject,
  signal,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { LibraryService } from '../../../services/library.service';
import {
  IngredientType,
  NewIngredientPayload,
} from '../../../shared/model/ingredient-interface';
import { CONSTANTS_DETAILS } from '../../../shared';
import { AuthService } from '../../../services/auth';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '../../../services/snackbar/snackbar.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatOptionModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { UpdateNutritionalInformationDialogComponent } from '../update-nutritional-information-dialog/update-nutritional-information-dialog.component';
import { DialogService } from '../../../services/dialog/dialog.service';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-new-ingredient-dialog',
  standalone: true,
  imports: [
    MatDialogModule,
    MatSlideToggleModule,
    MatFormFieldModule,
    TranslateModule,
    MatSelectModule,
    FormsModule,
    MatInputModule,
    MatOptionModule,
    MatButtonModule,
    MatTooltipModule,
  ],
  templateUrl: './new-ingredient-dialog.component.html',
  styleUrl: './new-ingredient-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewIngredientDialogComponent implements OnInit {
  private authService = inject(AuthService);
  public userProfile = this.authService.getAccountGroups();
  public durationInSeconds = CONSTANTS_DETAILS.MESSAGE_SHOW_TIME;
  public newIngredientDialog_ingredientName =
    CONSTANTS_DETAILS.APP_MENU_LABELS.INGREDIENT_NAME_PLACEHOLDER;
  public newIngredientDialog_ingredientType =
    CONSTANTS_DETAILS.APP_MENU_LABELS.INGREDIENT_TYPE_PLACEHOLDER;
  public ingredientTypes: IngredientType[] = [];

  public ingredientName = signal<string>('');
  public ingredientType = signal<string>('');
  public hasAddPrivileges: boolean = true;
  public hasUpdatePrivileges: boolean = false;

  ngOnInit(): void {
    this.getIngredientTypeList();
    this.authService.getRolesPrivileges().then((roles) => {
      this.hasAddPrivileges =
        !Array.isArray(roles) &&
        roles.privilegeIdentifiers &&
        roles.privilegeIdentifiers.includes('LIB_ADD_ING_NUTRINFO');
      this.hasUpdatePrivileges =
        !Array.isArray(roles) &&
        roles.privilegeIdentifiers &&
        roles.privilegeIdentifiers.includes('LIB_UPDT_ING_NUTRINFO');
    });
  }

  /**
   * Creates an instance of NewIngredientDialogComponent.
   * @param {LibraryService} _libraryService
   * @param {SnackbarService} _snackbarService
   * @param {TranslateService} _translate
   * @param {MatDialogRef<NewIngredientDialogComponent>} _dialogRef
   * @param {ChangeDetectorRef} _cdr
   * @param {DialogService} _dialogService
   * @memberof NewIngredientDialogComponent
   */
  constructor(
    private _libraryService: LibraryService,
    private _dialogRef: MatDialogRef<NewIngredientDialogComponent>,
    private _snackbarService: SnackbarService,
    private _translate: TranslateService,
    private _cdr: ChangeDetectorRef,
    private _dialogService: DialogService,
  ) {}

  /**
   * Set the ingredient name for new ingredient.
   *
   * @memberof NewIngredientDialogComponent
   */
  public ingredientNameInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    this.ingredientName.set(inputElement.value);
  }

  /**
   * Set the ingredient type for new ingredient.
   *
   * @memberof NewIngredientDialogComponent
   */
  public ingredientTypeSelected(event: MatSelectChange): void {
    this.ingredientType.set(event.value as string);
  }

  /**
   * Get the list of ingredient type list.
   *
   * @memberof NewIngredientDialogComponent
   */
  public getIngredientTypeList(): void {
    this._libraryService
      .getAllIngredientTypes()
      .then((response: { ingredientTypes: IngredientType[] }) => {
        if (response && response?.ingredientTypes) {
          this.ingredientTypes = response?.ingredientTypes.filter(
            (type: IngredientType) =>
              type.id !== 3 && type.id !== 5 && type.isEnabled,
          );
          this.ingredientTypes.sort((a, b) =>
            a?.identifier.localeCompare(b?.identifier),
          );
          this._cdr.detectChanges();
        }
      });
  }

  /**
   * Reset the values of new ingredient window.
   *
   * @memberof NewIngredientDialogComponent
   */
  public resetClick(): void {
    this.ingredientName.set('');
    this.ingredientType.set('');
  }

  /**
   * Disable the add nutrition button.
   *
   * @memberof NewIngredientDialogComponent
   */
  public disableButton(): boolean {
    return !this.ingredientType() || !this.ingredientName();
  }

  /**
   * Disable the add nutrition with nutrition data button.
   *
   * @memberof NewIngredientDialogComponent
   */
  public disableNutritionButton(): boolean {
    return (
      !this.ingredientType() || !this.ingredientName() || !this.hasAddPrivileges
    );
  }

  /**
   * Save the new ingredient name.
   *
   * @memberof NewIngredientDialogComponent
   */
  public saveIngredient(addNutritionInfo: boolean): void {
    const payload: NewIngredientPayload = {
      ingredientName: this.ingredientName(),
      ingredientTypeName: this.ingredientType(),
      ingredientTypeId: this.ingredientTypes.find(
        (type) => type.name === this.ingredientType(),
      )?.id,
    };
    this._libraryService.createIngredient(payload).then((response) => {
      if (response?.isSuccess && response?.ingredients) {
        this._snackbarService.open({
          message: this._translate.instant('ingredientCreatedMessage', {
            id: response?.ingredients?.ingredientIdentifier,
          }),
          type: 'success',
        });
        if (addNutritionInfo) {
          this._dialogService.openDialog(
            UpdateNutritionalInformationDialogComponent,
            {
              data: {
                ...response.ingredients,
                isEditable: true,
                ingredientTypeName: response.ingredientTypeName,
                hasAddPrivileges: this.hasAddPrivileges,
                hasUpdatePrivileges: this.hasUpdatePrivileges,
              },
              minWidth: '70rem',
              maxWidth: '80rem',
              disableClose: true,
              minHeight: '30rem',
              panelClass: 'popup-grid-dialog-view',
            },
          );
        }
        this._dialogRef.close();
      } else if (response?.message && !response?.isSuccess) {
        this._snackbarService.open({
          message: this._translate.instant('customIngredientErrorMessage', {
            message: response.message,
          }),
          type: 'error',
        });
      }
    });
  }
}
